// @ts-strict-ignore
import {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { UserContext } from '~/dashboard/context';
import {
  Box,
  TextField,
  MultiSelect,
  Separator,
  HelpText,
} from '@usealloy/component-library';
import type { FieldErrors } from 'react-hook-form';
import {
  Controller, useFormContext, useFieldArray, useFormState,
} from 'react-hook-form';

import { getActionName } from 'flow/service/actions';
import type { FormState, ServiceOption, DisplayNameOption } from './types';
import apiCalls from '~/helpers/apiCalls';
import { convertServiceConfigOption } from '../serviceLocalSettingHelpers';
import {
  getServiceFailureDefaultOptions, convertActionOptionsToServiceOptions,
} from '../serviceOptionHelpers';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { setServiceActionOptionPublicIdInEditing, updateServiceActionValidation, updateServiceConfigInWorkflowProps } from 'flow/store/redux/slice';
import {
  selectIsOnboardingWorkflow, selectFocusedAction, selectRecurringWorkflows, selectServices,
  selectWorkflowFailureHandlingMode,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { ServiceConfigOption } from '~/dashboard/views/settings/service-configuration/ServiceConfigOptionView';
import type { FormattedServiceConfigOption, ServiceConfig } from '~/dashboard/views/settings/service-configuration/types';
import { DefaultLevel, ServiceConfigModalMode } from '~/dashboard/views/settings/service-configuration/types';

import { generatePublicId } from 'flow/service/publicIds';
import {
  ValidationTextField,
  RestoreLink,
} from '~/dashboard/views/settings/service-configuration/styles';
import { DISPLAY_NAME_EMPTY_ERROR } from '../constants';
import {
  StyledLocalSettingContainer,
} from './styles';
import { DISPLAY_NAME_OPTION_KEY, WEBHOOK_KEY } from 'flow/service/actionOptions';
import RecurringWebhookContainer from '../ServiceOptionsTab/Sections/RecurringWebhook';
import type { NodeEditorAutoSaveTabProps } from '../../useNodeEditorForm/types';
import { useDebouncedFunction } from '../../useNodeFormAutoSave';
import { failureHandlingDescriptionBestEffortMode, failureHandlingDescriptionFullThresholdMode } from '../../constants';

export default function ServiceLocalSettingsTab({
  handleAutoSave,
  isReadOnly,
}: NodeEditorAutoSaveTabProps) {
  const dispatch = useAppDispatch();
  const focusedAction = useAppSelector(selectFocusedAction);
  const isOnboardingWorkflow = useAppSelector(selectIsOnboardingWorkflow);
  const recurringWorkflowsByService = useAppSelector(selectRecurringWorkflows);
  const failureHandlingModeValue = useAppSelector(selectWorkflowFailureHandlingMode);
  const services = useAppSelector(selectServices);
  const { typhonDomain } = useContext(UserContext);
  const service = Object.values(
    services,
  ).find(serviceObj => (serviceObj.internal_name === focusedAction.service.internalName));
  const {
    service_id: serviceId,
    serviceConfig: initServiceConfig,
    production_enabled: productionEnabled,
    sandbox_enabled: sandboxEnabled,
  } = service;

  const [serviceConfig, setServiceConfig] = useState<ServiceConfig>(initServiceConfig);
  const [isServiceDataFetched, setIsServiceDataFetched] = useState<boolean>();
  const [isAddingOption, setIsAddingOption] = useState(false);

  const serviceFailureOptions = useMemo(() => getServiceFailureDefaultOptions(), []);
  const serviceOptions = useMemo(
    () => focusedAction && convertActionOptionsToServiceOptions(focusedAction.options),
    [focusedAction],
  );

  useEffect(() => {
    const fetchServiceData = async () => {
      if (!serviceConfig) {
        const serviceConfigData: ServiceConfig = await apiCalls.settings.getServiceConfig(
          { typhonDomain, serviceId },
        );

        dispatch(updateServiceConfigInWorkflowProps({
          serviceConfig: serviceConfigData,
          internalName: focusedAction?.service.internalName,
        }));

        setServiceConfig(serviceConfigData);
      }
      setIsServiceDataFetched(true);
    };

    fetchServiceData();
  }, [typhonDomain, serviceId, focusedAction, serviceConfig, dispatch]);

  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    handleSubmit,
    setError,
    clearErrors,
  } = useFormContext<FormState>();
  const { errors } = useFormState({ control });

  const {
    append: appendServiceConfigOption,
    fields: formattedServiceConfigOptions,
    remove: removeServiceConfigOption,
    replace: replaceServiceConfigOption,
  } = useFieldArray({
    name: 'formattedServiceConfigOptions',
    control,
  });

  const watchFieldArray = watch('formattedServiceConfigOptions');
  const controlledFields = formattedServiceConfigOptions.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  /*
    * Since this form fetches data within it and has a non-traditional render cycle, we need to
    * invoke `handleAutoSave` manually when things change instead of relying on handleNodeFormAutoSave
    * like the other forms
    */
  const debouncedAutoSaveFn = useCallback((formData: FormState) => {
    handleAutoSave(formData);
    dispatch(updateServiceActionValidation({
      publicId: focusedAction.publicId,
      data: [],
    }));
  }, [dispatch, focusedAction.publicId, handleAutoSave]);

  const debouncedAutoSave = useDebouncedFunction<FormState>({
    fn: debouncedAutoSaveFn,
  });

  const debouncedAutoSaveOnErrorFn = useCallback((formErrors: FieldErrors<FormState>) => {
    const displayNameOption = getValues('displayNameOption');
    const errorMessages = formErrors?.formattedServiceConfigOptions?.map(
      e => `${displayNameOption?.value}: ${e.value?.message}`,
    );
    dispatch(updateServiceActionValidation({
      publicId: focusedAction.publicId,
      data: errorMessages,
    }));
  }, [dispatch, focusedAction.publicId, getValues]);

  const debouncedAutoSaveOnError = useDebouncedFunction<FieldErrors<FormState>>({
    fn: debouncedAutoSaveOnErrorFn,
  });

  const submitAutoSave = useCallback(
    () => handleSubmit(debouncedAutoSave, debouncedAutoSaveOnError)(),
    [debouncedAutoSave, debouncedAutoSaveOnError, handleSubmit],
  );

  const serviceName = focusedAction.service.name;
  const focusedActionOptions = focusedAction.options;

  const restoreDisplayNameToServiceName = useCallback(() => {
    clearErrors('displayNameOption.value');
    setValue('displayNameOption.value', serviceName, { shouldValidate: true, shouldDirty: true });
    submitAutoSave();
  }, [clearErrors, serviceName, setValue, submitAutoSave]);

  const commitDisplayNameField = useCallback((rawInput: string) => {
    const trimmed = rawInput.trim();
    if (trimmed.length === 0) {
      // if empty input - revert back to saved action option value or fall back to service name
      clearErrors('displayNameOption.value');
      const displayName = focusedActionOptions[DISPLAY_NAME_OPTION_KEY]?.value;
      const revert = displayName || serviceName;
      setValue('displayNameOption.value', String(revert), { shouldValidate: true, shouldDirty: true });
    } else if (rawInput !== trimmed) {
      // if trimmed - need to persist in form
      setValue('displayNameOption.value', trimmed, { shouldValidate: true, shouldDirty: true });
    }
    submitAutoSave();
  }, [clearErrors, focusedActionOptions, serviceName, setValue, submitAutoSave]);

  useEffect(() => {
    if (controlledFields.length < 1 && serviceConfig) {
      let initialRecurringWebhook: ServiceOption = null;

      const {
        credentialOwnerOption: initCredOwnerOpt,
        formattedServiceConfigOptions: initSvcConfigOpts,
      } = convertServiceConfigOption(
        serviceConfig,
        focusedAction.options,
        focusedAction.service.credentialOwner,
      );

      serviceOptions.forEach(option => {
        /*
          Here we direct and handle action options for specific sections:
          1. handle recurring webhook separately
        */
        if (WEBHOOK_KEY === option.key) {
          initialRecurringWebhook = option;
        }
      });
      const displayNameIndex = initSvcConfigOpts.findIndex(
        option => option.key === DISPLAY_NAME_OPTION_KEY,
      );
      if (displayNameIndex >= 0) {
        const displayNameOptions: Array<FormattedServiceConfigOption> = initSvcConfigOpts.splice(
          displayNameIndex, 1,
        );
        setValue('displayNameOption', displayNameOptions[0] as DisplayNameOption);
      } else {
        const serviceConfigOption = serviceConfig.serviceConfigOptions.find(
          option => option.slug === DISPLAY_NAME_OPTION_KEY,
        );
        const initialValue = getActionName(focusedAction);
        setValue('displayNameOption', {
          serviceConfigOptionId: serviceConfigOption?.serviceConfigOptionId,
          isAdding: false,
          value: initialValue,
          defaultValue: initialValue,
          isUsingDefault: false,
          defaultLevel: DefaultLevel.ALLOY,
          key: DISPLAY_NAME_OPTION_KEY,
          publicId: generatePublicId('actionoption'),
        } as DisplayNameOption);
      }

      setValue('recurringWorkflow', initialRecurringWebhook);
      setValue('credentialOwnerOption', initCredOwnerOpt);
      replaceServiceConfigOption(initSvcConfigOpts);
    }
  }, [
    serviceOptions, isServiceDataFetched, serviceConfig,
    service.name, focusedAction, controlledFields.length,
    replaceServiceConfigOption, setValue,
  ]);

  const serviceConfigOptionsMapping = serviceConfig?.serviceConfigOptions?.reduce(
    (acc, option) => {
      acc[option.serviceConfigOptionId] = option;
      return acc;
    }, {},
  );

  const handlingDescription = failureHandlingModeValue === 'full-threshold'
    ? failureHandlingDescriptionFullThresholdMode : failureHandlingDescriptionBestEffortMode;

  return (
    <StyledLocalSettingContainer>
      <Box gap={2} justifyContent="flex-start" alignItems="flex-start">
        <Box
          direction="column"
          gap={2}
          justifyContent="flex-start"
          style={{ padding: 'var(--cl-space-0) var(--cl-space-4)' }}
        >
          <Controller
            control={control}
            name="displayNameOption.value"
            rules={{
              validate: value => (
                (value != null && String(value).trim().length >= 1)
                || DISPLAY_NAME_EMPTY_ERROR
              ),
            }}
            render={({ field: { onChange, value, onBlur } }) => {
              const trimmedValue = String(value ?? '').trim();
              const trimmedServiceName = String(serviceName).trim();
              const showRestoreNameLink = !isReadOnly
                && trimmedValue !== trimmedServiceName;
              const displayNameFieldError = errors.displayNameOption?.value?.message;

              return (
                <Box
                  data-dd-action-name="service-local-settings-display-name"
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="stretch"
                  style={{ width: '100%' }}
                >
                  <TextField
                    label="Display Name"
                    type="text"
                    helpText={(
                      <Box justifyContent="flex-start" direction="row" style={{ width: '100%' }}>
                        <Box justifyContent="flex-start" style={{ flex: 1, minWidth: 0 }}>
                          <ValidationTextField>
                            Changes the name that is displayed for this service in the workflow node
                          </ValidationTextField>
                        </Box>
                        {showRestoreNameLink && (
                          <Box
                            justifyContent="flex-end"
                            style={{ width: '30%', flexShrink: 0 }}
                          >
                            <RestoreLink
                              onClick={e => {
                                e.preventDefault();
                                restoreDisplayNameToServiceName();
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  restoreDisplayNameToServiceName();
                                }
                              }}
                            >
                              restore name
                            </RestoreLink>
                          </Box>
                        )}
                      </Box>
                    )}
                    error={displayNameFieldError || false}
                    value={value}
                    onChange={e => {
                      onChange(e);
                      const trimmed = String(e.target.value ?? '').trim();
                      if (!trimmed) {
                        setError(
                          'displayNameOption.value',
                          { type: 'manual', message: DISPLAY_NAME_EMPTY_ERROR },
                          { shouldFocus: false },
                        );
                      } else {
                        clearErrors('displayNameOption.value');
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      }
                    }}
                    onBlur={e => {
                      onBlur();
                      commitDisplayNameField(e.target.value ?? '');
                    }}
                    disabled={isReadOnly}
                  />
                </Box>
              );
            }}
          />

          {isOnboardingWorkflow
            && recurringWorkflowsByService[focusedAction.service.name]
            && (
              <Controller
                name="recurringWorkflow"
                control={control}
                render={({ field: { onChange } }) => (
                  <RecurringWebhookContainer
                    initialSelectedRecurringWorkflow={serviceOptions.find(
                      option => option.key === WEBHOOK_KEY)}
                    recurringWorkflowOptions={
                      recurringWorkflowsByService[focusedAction.service.name]
                    }
                    onChange={e => {
                      onChange(e);
                      submitAutoSave();
                    }}
                    readOnly={isReadOnly}
                  />
                )}
              />
            )}
        </Box>
        <Box
          direction="column"
          gap={2}
          style={{ padding: 'var(--cl-space-0) var(--cl-space-4) var(--cl-space-1)' }}
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          <Controller
            name="serviceFailureDefaultOption"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MultiSelect
                isMulti={false}
                label="Service Failure Handling - if data is missing:"
                helpText={handlingDescription}
                onChange={e => {
                  onChange(e);
                  submitAutoSave();
                }}
                options={serviceFailureOptions}
                selectedOptions={value}
                isDisabled={isReadOnly}
              />
            )}
          />
          <HelpText>
            Controls what action to take if the Service fails to run
          </HelpText>
        </Box>
      </Box>
      <Separator />
      {serviceConfig && (
        <ServiceConfigOption
          mode={ServiceConfigModalMode.NODE}
          serviceConfigOptionsMapping={serviceConfigOptionsMapping}
          formattedServiceConfigOptions={controlledFields}
          watch={watch}
          errors={errors}
          control={control}
          setValue={setValue}
          trigger={trigger}
          appendServiceConfigOption={appendServiceConfigOption}
          removeServiceConfigOption={removeServiceConfigOption}
          selectedAlloyOptions={serviceConfig.selectedAlloyOptions}
          selectedCustomerOptions={serviceConfig.selectedCustomerOptions}
          isAddingOption={isAddingOption}
          setIsAddingOption={
            isAdding => {
              setIsAddingOption(isAdding);
              if (isAdding) {
                dispatch(setServiceActionOptionPublicIdInEditing('true'));
              } else {
                dispatch(setServiceActionOptionPublicIdInEditing(null));
              }
            }
          }
          serviceName={service.name}
          serviceId={serviceId}
          submitAutoSave={submitAutoSave}
          onlySandboxEnabled={!productionEnabled && sandboxEnabled}
        />
      )}
    </StyledLocalSettingContainer>
  );
}
